function safeFromCodePoint(n: number): string {
  if (!Number.isFinite(n) || n < 0 || n > 0x10ffff) return '';
  try {
    return String.fromCodePoint(n);
  } catch {
    return '';
  }
}

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#(\d+);/g, (_, d) => safeFromCodePoint(Number(d)))
    .replace(/&#x([\da-fA-F]+);/gi, (_, h) =>
      safeFromCodePoint(parseInt(h, 16)),
    )
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/** True when value is only `<img …>` markup (audit PatternClass = img-only). */
export function isImgOnlyHtml(raw: string): boolean {
  const trimmed = raw.trim();
  if (!/<img\b/i.test(trimmed)) return false;
  const withoutImg = trimmed
    .replace(/<img\b[^>]*\/?>/gi, '')
    .replace(/\s+/g, '');
  return withoutImg === '';
}

/** First `src` from an `<img>` tag, or null. */
export function extractFirstImgSrc(raw: string): string | null {
  const match = raw.match(/<img\b[^>]*\ssrc\s*=\s*["']?([^"'>\s]+)/i);
  return match?.[1] ?? null;
}

/** First `<img …>` tag in mixed ILR2 markup, or null. */
function extractFirstImgMarkup(raw: string): string | null {
  const match = raw.match(/<img\b[^>]*\/?>/i);
  return match?.[0] ?? null;
}

export type MixedIlr2HtmlParts = {
  plainText: string;
  imgMarkup: string | null;
};

/**
 * Splits mixed ILR2 text (e.g. sire name + trait badge) into plain label and
 * optional img-only fragment for {@link Ilr2StructuredImgCell}.
 */
export function parseMixedIlr2Html(
  raw: string | null | undefined,
): MixedIlr2HtmlParts {
  if (raw == null || raw === '') {
    return { plainText: '', imgMarkup: null };
  }
  const s = String(raw);
  if (isImgOnlyHtml(s)) {
    return { plainText: '', imgMarkup: s.trim() };
  }
  return {
    plainText: displayPlainText(s),
    imgMarkup: extractFirstImgMarkup(s),
  };
}

/** Remove HTML tags until stable (handles nested/malformed markup). */
function removeHtmlTags(raw: string): string {
  let result = raw;
  let previous = '';
  while (result !== previous) {
    previous = result;
    result = result.replace(/<[^>]+>/g, '');
  }
  return result;
}

/**
 * Strips legacy ILR2 HTML to readable plain text (default display mode).
 * Removes tags (including `<br>`), decodes common entities, collapses whitespace.
 */
export function stripHtmlToPlainText(raw: string): string {
  let s = raw.replace(/\s*<br\s*\/?>\s*/gi, ' ');
  s = removeHtmlTags(s);
  s = decodeHtmlEntities(s);
  return s.replace(/\s+/g, ' ').trim();
}

/** Plain text for labels, grid cells, and detail values — never render raw tags. */
export function displayPlainText(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const s = String(value);
  if (isImgOnlyHtml(s)) return '';
  return stripHtmlToPlainText(s);
}

/** True when ILR2 stored `<br>` line breaks (detail pages may honour these). */
export function hasIlr2LineBreaks(raw: string | null | undefined): boolean {
  if (raw == null || raw === '') return false;
  return /<br\s*\/?>/i.test(String(raw));
}

/** Split ILR2 multiline HTML into plain-text lines (tags stripped per line). */
export function splitIlr2MultilinePlainParts(
  raw: string | null | undefined,
): string[] {
  if (raw == null || raw === '') return [];
  return String(raw)
    .split(/\s*<br\s*\/?>\s*/gi)
    .map((part) => stripHtmlToPlainText(part))
    .filter((part) => part !== '');
}

/** Animal-detail multiline values: honour ILR2 `<br>` line breaks only. */
export function splitDetailMultilinePlainParts(
  raw: string | null | undefined,
): string[] {
  if (raw == null || raw === '') return [];

  const parts = hasIlr2LineBreaks(raw)
    ? splitIlr2MultilinePlainParts(raw)
    : (() => {
        const plain = displayPlainText(raw);
        return plain ? [plain] : [];
      })();

  return parts.filter((part) => !isStrippedIlr2LinkPlaceholder(part));
}

/**
 * ILR2 hyperlink anchor text left after tag stripping (e.g. CAHF EPD Information).
 * i4 rendered these as links; without the URL the label has no value — hide the field.
 */
const STRIPPED_ILR2_LINK_PLACEHOLDER_RE = /^Click for (?:EPD|EBV) info\.?$/i;

export function isStrippedIlr2LinkPlaceholder(
  plain: string | null | undefined,
): boolean {
  if (plain == null || plain === '') return false;
  return STRIPPED_ILR2_LINK_PLACEHOLDER_RE.test(plain.trim());
}

/** `undefined` when empty after stripping — suitable for `title={…}`. */
export function plainTextForTooltip(
  value: string | null | undefined,
): string | undefined {
  const plain = displayPlainText(value);
  return plain === '' ? undefined : plain;
}
